import { useEffect, useMemo, useRef, useState } from 'react'
import type { PlanRow } from '../api/types'
import { ChevronDownIcon, CloseIcon, PlanIcon, PriorityIcon } from '../components/Icons'
import { stripWords } from '../plans/words'
import { Popover } from '../components/Popover'
import { cx } from '../transcript/bits'
import { useBoardState } from '../store/context'
import { senderName } from '../sessions/words'
import { PRIORITY_ORDER, priorityName } from '../vocab'
import { SHOW_CLOSED } from './Column'
import { claimId } from './filters'
import type { UpdateView } from './useBoardView'
import { clearFilters, hasFilters, SORT_MODES, toggle, type BoardView, type SortMode } from './view'

export function FilterBar({ view, onChange, shown, plan }: { view: BoardView; onChange: UpdateView; shown: number; plan?: PlanRow }) {
  const { labels, issues, selectedProjectId, me } = useBoardState()
  const [text, setText] = useState(view.q)
  const pushed = useRef(view.q)
  useEffect(() => {
    if (view.q !== pushed.current) { pushed.current = view.q; setText(view.q) }
  }, [view.q])
  useEffect(() => {
    if (text === pushed.current) return
    const timer = setTimeout(() => { pushed.current = text; onChange(v => ({ ...v, q: text })) }, 150)
    return () => clearTimeout(timer)
  }, [text, onChange])
  const labelList = useMemo(() => [...labels.values()].filter(l => l.project === selectedProjectId && !l.deleted_at)
    .sort((a, b) => a.name.localeCompare(b.name)), [labels, selectedProjectId])
  const activeLabels = view.labels.filter(name => labelList.some(l => l.name.toLowerCase() === name))
  const claimers = useMemo(() => {
    const found = new Map<string, string>()
    for (const issue of issues.values()) if (issue.project === selectedProjectId && issue.claim) found.set(claimId(issue.claim.by), senderName(issue.claim.by, me))
    return [...found].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [issues, selectedProjectId])
  return (
    <div className="filter-bar" role="toolbar" aria-label="Board filters">
      <label className="filter-text">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" aria-hidden="true"><circle cx="7" cy="7" r="4.5" /><path d="m10.5 10.5 3 3" /></svg>
        <input type="search" value={text} onChange={e => setText(e.target.value)} placeholder="Filter" aria-label="Filter issues by text" />
      </label>
      <Popover label="Labels" buttonClassName={cx('filter-button', activeLabels.length > 0 && 'is-active')}
        button={<>Labels{activeLabels.length > 0 && <span className="filter-badge">{activeLabels.length}</span>}<ChevronDownIcon size={12} /></>}>
        {() => <div className="menu">{labelList.map(label => <label key={label.id} className="menu-check">
          <input type="checkbox" checked={view.labels.includes(label.name.toLowerCase())}
            onChange={() => onChange(v => ({ ...v, labels: toggle(v.labels, label.name.toLowerCase()) }))} />
          <span className="label-dot" style={{ background: label.colour }} />{label.name}
        </label>)}</div>}
      </Popover>
      <Popover label="Priority" buttonClassName={cx('filter-button', view.priorities.length > 0 && 'is-active')}
        button={<>Priority{view.priorities.length > 0 && <span className="filter-badge">{view.priorities.length}</span>}<ChevronDownIcon size={12} /></>}>
        {() => <div className="menu">{PRIORITY_ORDER.map(priority => <label key={priority} className="menu-check">
          <input type="checkbox" checked={view.priorities.includes(priority)}
            onChange={() => onChange(v => ({ ...v, priorities: toggle(v.priorities, priority) }))} />
          <PriorityIcon priority={priority} />{priorityName(priority)}
        </label>)}</div>}
      </Popover>
      <label className={cx('filter-select', view.claimed && 'is-active')}>
        <span className="visually-hidden">Claimed</span>
        <select value={view.claimed} onChange={e => onChange(v => ({ ...v, claimed: e.target.value }))}>
          <option value="">Any claim</option><option value="yes">Claimed</option><option value="no">Unclaimed</option>
          {claimers.map(a => <option key={a.id} value={a.id}>Claimed by {a.name}</option>)}
        </select>
      </label>
      <label className="filter-select">
        <span className="visually-hidden">Sort</span>
        <select value={view.sort} onChange={e => onChange(v => ({ ...v, sort: e.target.value as SortMode }))}>
          {SORT_MODES.map(mode => <option key={mode.value} value={mode.value}>Sort: {mode.name}</option>)}
        </select>
      </label>
      {view.plan && (
        <span className="filter-pill" title={plan?.title}>
          <PlanIcon size={12} />
          <span className="filter-pill-text">{plan ? plan.title : stripWords.unknown}</span>
          <button type="button" onClick={() => onChange((v) => ({ ...v, plan: null }))} aria-label={stripWords.whole}>
            <CloseIcon size={12} />
          </button>
        </span>
      )}
      {hasFilters({ ...view, labels: activeLabels }) && <>
        <span className="filter-count" aria-live="polite">{shown} {shown === 1 ? 'issue' : 'issues'}</span>
        <button type="button" className="button button-quiet" onClick={() => { pushed.current = ''; setText(''); onChange(clearFilters) }}>Clear filters</button>
      </>}
      <button type="button" className="button button-small button-quiet board-closed" onClick={() => onChange(v => ({ ...v, closed: !v.closed }))}>
        {view.closed ? 'Hide Cancelled and Duplicate' : SHOW_CLOSED}
      </button>
    </div>
  )
}
